import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { TextField, TextFieldProps, Tooltip, Typography, useTheme } from "@mui/material";
import { forwardRef, useId } from "react";

export type TextInputProps = Omit<TextFieldProps, "onChange"> & {
	label: string;
	onChange?: (value: string) => void;
	helperText?: string;
	error?: boolean;
	labelTooltip?: string;
	valueTooltip?: string;
};

const TextInput = forwardRef<HTMLDivElement, TextInputProps>(
	(
		{ label, onChange, id, error, helperText, required, labelTooltip, valueTooltip, ...props },
		ref
	) => {
		const theme = useTheme();
		const generatedId = useId();
		const inputId = id ?? generatedId;

		return (
			<div style={{ width: props.fullWidth ? "100%" : "auto" }} ref={ref}>
				<Typography
					variant="text12Uppercase"
					component="label"
					htmlFor={inputId}
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.5,
						color: error ? "error.main" : "secondary.light50",
						mb: 0.5
					}}
				>
					{label} {required && "*"}
					{labelTooltip && (
						<Tooltip
							title={labelTooltip}
							placement="top-start"
							enterTouchDelay={0}
							leaveTouchDelay={3000}
							slotProps={{
								popper: { modifiers: [{ name: "offset", options: { offset: [0, -8] } }] }
							}}
						>
							<InfoOutlinedIcon sx={{ fontSize: "16px", cursor: "default" }} />
						</Tooltip>
					)}
				</Typography>

				{(() => {
					const field = (
						<TextField
							{...props}
							size="small"
							id={inputId}
							error={error}
							required={required}
							helperText={helperText}
							sx={{
								"& .MuiFormHelperText-root": {
									mx: 0
								},
								...(props.sx as object)
							}}
							fullWidth={props.fullWidth ?? true}
							value={props.value === null || props.value === undefined ? "" : props.value}
							onChange={(e) => onChange?.(e.target.value)}
							variant={props.variant ?? "outlined"}
						/>
					);

					return valueTooltip ? (
						<Tooltip
							title={valueTooltip}
							enterTouchDelay={0}
							leaveTouchDelay={3000}
							slotProps={{ tooltip: { sx: { wordBreak: "break-word" } } }}
						>
							<span style={{ display: "block" }}>{field}</span>
						</Tooltip>
					) : (
						field
					);
				})()}
			</div>
		);
	}
);

TextInput.displayName = "TextInput";

export default TextInput;
